import { useCallback, useState, useEffect } from "react";
import type { ChangeEvent } from "react";
import { Box, Typography, IconButton, Button, TextField } from "../ui";
import type { Theme } from "../ui";
import { DescriptionIcon, VisibilityIcon, DeleteIcon } from "../ui";
import DocumentPreviewDialog from "./uu/uuUi/DocumentPreviewDialog";
import { DocumentsPageService } from "../shared/api/services";
import { Checkbox } from "@mui/material";
import { useAlert } from "../app/providers/AlertProvider";
import { UI_MESSAGES } from "../constants/ui";
import type { Company } from "./AddCompanyForm";
import type { UpdateDocumentRequest } from "@/shared/api/types";

type DocumentListItem = {
  id: string | number;
  name: string;
  size?: string | number;
  uploadDate?: string;
  uploadedDate?: string;
  description?: string;
  documentName?: string;
  showNotes?: boolean;
  toggleNotes?: (id: string | number, value: boolean) => void;
  documentId?: string | number;
  secondId?: string | number;
  apiObject?: UpdateDocumentRequest | {
    documentId?: string | number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const inferMimeType = (fileName?: string): string => {
  if (!fileName) return "application/octet-stream";

  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "pdf":
      return "application/pdf";
    default:
      return "application/octet-stream";
  }
};

interface DocumentsListProps {
  documents: DocumentListItem[];
  /** Required when `showCheckbox` is true (e.g. share flow). */
  company?: Company;
  showCheckbox?: boolean;
  onOpen?: (doc: DocumentListItem) => void | Promise<void>;
  onDelete: (id: string | number) => void | Promise<void>;
  /** Required when `showCheckbox` is true. */
  onCheck?: (id: string | number) => void | Promise<void>;
  onNameChange: (doc: DocumentListItem, value: string) => void;
  notesField?: keyof DocumentListItem & string;
}

const getNotesValue = (
  doc: DocumentListItem,
  field?: keyof DocumentListItem & string
): string => {
  if (!field) {
    return "";
  }
  const value = doc[field];
  if (typeof value === "string") {
    return value;
  }
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
};

/** Matches dashboard / UW document upload “Notes (optional)” link colour */
const NOTES_TOGGLE_BUTTON_SX = {
  textTransform: "none" as const,
  ml: -1,
  mt: 1,
  width: 150,
  fontWeight: 400,
  color: "#0d9488",
  "&:hover": {
    backgroundColor: "rgba(13, 148, 136, 0.06)",
  },
};

export default function DocumentsList({
  documents,
  showCheckbox,
  onOpen,
  onDelete,
  onCheck,
  company,
  onNameChange,
  notesField = "documentName",
}: DocumentsListProps) {
  const { showAlert } = useAlert();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string | null>(
    null
  );
  const [previewIsLoading, setPreviewIsLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewDocumentName, setPreviewDocumentName] =
    useState<string>("Document Preview");
  const [previewMimeType, setPreviewMimeType] = useState<string>("");

  // Clean up blob URL when dialog closes
  useEffect(() => {
    if (!previewOpen && previewDocumentUrl) {
      URL.revokeObjectURL(previewDocumentUrl);
      setPreviewDocumentUrl(null);
    }
  }, [previewOpen, previewDocumentUrl]);

  const getDocumentId = (doc: DocumentListItem): string | number | null => {
    // Try multiple possible locations for documentId
    if (doc.documentId) {
      return doc.documentId;
    }
    if (doc.apiObject?.documentId) {
      return doc.apiObject.documentId;
    }
    // Fallback to id if it looks like a documentId
    if (doc.id) {
      return doc.id;
    }
    return null;
  };

  const handleView = useCallback(
    async (doc: DocumentListItem) => {
      const api = doc.apiObject;
      const hotUrl =
        api &&
        typeof api === "object" &&
        "hotStorageUrl" in api &&
        typeof (api as { hotStorageUrl?: unknown }).hotStorageUrl === "string"
          ? (api as { hotStorageUrl: string }).hotStorageUrl
          : undefined;

      if (hotUrl) {
        setPreviewOpen(true);
        setPreviewDocumentName(doc.name || "Document Preview");
        setPreviewDocumentUrl(hotUrl);
        setPreviewIsLoading(false);
        setPreviewError(null);
        setPreviewMimeType(inferMimeType(doc.name));
        return;
      }

      const documentId = doc.apiObject?.documentId;

      // If no documentId found, fall back to onOpen if provided
      if (!documentId) {
        if (onOpen) {
          Promise.resolve(onOpen(doc)).catch(() => undefined);
        }
        return;
      }

      // Open dialog immediately with loading state
      setPreviewOpen(true);
      setPreviewDocumentName(doc.name || "Document Preview");
      setPreviewDocumentUrl(null);
      setPreviewIsLoading(true);
      setPreviewError(null);
      setPreviewMimeType("");

      try {
        if (typeof window === "undefined") {
          setPreviewError("Preview is not supported in this environment.");
          setPreviewIsLoading(false);
          return;
        }

        const data =
          await DocumentsPageService.downloadCustomerDocument(documentId);

        const blob =
          data instanceof Blob
            ? new Blob([data], { type: data.type || inferMimeType(doc.name) })
            : new Blob([data as unknown as BlobPart], {
                type: inferMimeType(doc.name),
              });

        const url = URL.createObjectURL(blob);
        setPreviewDocumentUrl(url);
        setPreviewMimeType(blob.type || inferMimeType(doc.name));
        setPreviewIsLoading(false);
      } catch (error) {
        console.error("Preview failed:", error);
        setPreviewError("Unable to download that document right now.");
        setPreviewIsLoading(false);
      }
    },
    [onOpen]
  );

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewError(null);
    setPreviewMimeType("");
  }, []);

  const handleDelete = useCallback(
    (id: string | number) => {
      Promise.resolve(onDelete(id)).catch(() => undefined);
    },
    [onDelete]
  );

  const handleCheck = useCallback(
    (id: string | number) => {
      if (!onCheck) return;
      Promise.resolve(onCheck(id)).catch(() => undefined);
    },
    [onCheck]
  );

  return (
    <>
      <DocumentPreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        documentName={previewDocumentName}
        documentUrl={previewDocumentUrl}
        isLoading={previewIsLoading}
        error={previewError}
        mimeType={previewMimeType}
      />
      {documents.map((doc) => (
        <Box
          key={doc.id}
          sx={{
            gap: 2,
            p: 1.5,
            mb: 2,
            backgroundColor: "#fff",
            border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#DCFCE7",
                borderRadius: 2,
                mr: 2,
              }}
            >
              <DescriptionIcon sx={{ color: "#165F54" }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: (theme: Theme) => theme.palette.text.secondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.size} • Uploaded {doc.uploadDate || doc.uploadedDate}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton aria-label="view" onClick={() => handleView(doc)}>
                <VisibilityIcon sx={{ color: "#626A77" }} />
              </IconButton>

              {showCheckbox &&
              company &&
              onCheck &&
              !doc.isNewlyUploaded ? (
                <Checkbox
                  size="medium"
                  onClick={() => handleCheck(doc.id)}
                  checked={
                    doc.secondId != null &&
                    company.selectedDocuments.some(
                      (x) => String(x) === String(doc.secondId)
                    )
                  }
                />
              ) : (
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDelete(doc.id)}
                >
                  <DeleteIcon sx={{ color: "#FF0000" }} />
                </IconButton>
              )}
            </Box>
          </Box>

          <>
            {!doc.showNotes && (
              <Button
                variant="text"
                onClick={() => doc.toggleNotes?.(doc.id, true)}
                sx={NOTES_TOGGLE_BUTTON_SX}
              >
                + Notes (optional)
              </Button>
            )}

            {doc.showNotes && (
              <Box sx={{ ml: -1 }}>
                <Button
                  variant="text"
                  onClick={() => doc.toggleNotes?.(doc.id, false)}
                  sx={{
                    ...NOTES_TOGGLE_BUTTON_SX,
                    mb: 0,
                  }}
                >
                  - Notes (optional)
                </Button>

                <TextField
                  value={getNotesValue(doc, notesField)}
                  onKeyDown={(e) => {
                    const val = getNotesValue(doc, notesField);
                    if (
                      val.length >= 200 &&
                      e.key.length === 1 &&
                      !e.ctrlKey &&
                      !e.metaKey
                    ) {
                      showAlert({
                        severity: "warning",
                        message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                      });
                    }
                  }}
                  onChange={(
                    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => onNameChange(doc, e.target.value)}
                  fullWidth
                  multiline
                  minRows={1}
                  placeholder="Please provide any additional information about this document"
                  variant="outlined"
                  InputProps={{
                    sx: { backgroundColor: "white" },
                  }}
                  sx={{ paddingLeft: 1, mt: 1 }}
                  inputProps={{
                    maxLength: 200,
                    onPaste: (e) => {
                      const val = getNotesValue(doc, notesField);
                      const pastedText = e.clipboardData.getData("text");
                      const selectionStart = (e.target as any).selectionStart || 0;
                      const selectionEnd = (e.target as any).selectionEnd || 0;
                      if (
                        val.length - (selectionEnd - selectionStart) + pastedText.length >
                        200
                      ) {
                        showAlert({
                          severity: "warning",
                          message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                        });
                      }
                    },
                  }}
                />
              </Box>
            )}
          </>
        </Box>
      ))}
    </>
  );
}
